<script lang="ts" setup>
import { computed, ref } from 'vue';
import { RouteLocationRaw } from 'vue-router';
import {
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmInput,
} from 'tir-components';
import { ArrowPathIcon } from 'tir-style-system/icons/outline';
import { ArrowDownTrayIcon } from 'tir-style-system/icons/solid';
import { ResponseModel } from '@/models';
import { AdapterService } from './adapter';
import {
  CheckTaskExistResultModel,
  GetWeeekTaskModel,
  WeeekTaskCheckExistsRequestModel,
} from './models';
import { TaskImportDataModel } from '../../models';

const adapter = new AdapterService();

const emit = defineEmits<{
  import: [data: TaskImportDataModel];
}>();

const props = defineProps({
  /**Ранее уже был импорт */
  isReimport: {
    type: Boolean,
    default: false,
  },
  /**ID проекта */
  projectId: {
    type: Number,
    required: true,
  },
  /**ID очереди */
  queueId: {
    type: String,
    required: true,
  },
});

/**Значение в инпуте */
const inputValue = ref('');
/**Ошибка в поле */
const isError = ref(false);
/**Сообщение об ошибке */
const errorMessage = ref('');
/**Информация о уже созданной задаче */
const taskExist = ref<CheckTaskExistResultModel>();

const taskExistRoute = computed<RouteLocationRaw>(() => ({
  name: 'task-detail',
  params: {
    projectId: taskExist.value.ProjectId,
    namespaceId: taskExist.value.NamespaceId,
    queueId: taskExist.value.QueueTaskId,
    taskId: taskExist.value.TaskId,
  },
}));
/**Загрузка */
const isLoading = ref(false);
/**Убрать ошибку */
const removeError = () => {
  isError.value = false;
  taskExist.value = undefined;
  errorMessage.value = '';
};

const getErrorMessage = (response: ResponseModel<any>) => {
  switch (response.ErrorCode) {
    case 1045:
      return 'Произошла ошибка импорта';
    case 1044:
      return 'Укажите ключ для авторизации токена';
    case 1043:
      return 'Задача не найдена';
    default:
      return response.ErrorMessage ?? 'Произошла ошибка';
  }
};
/**
 * Обработчик ошибки при импорте
 * @param response Ответ метода
 */
const onErrorImport = (response: ResponseModel<any>) => {
  isError.value = true;
  errorMessage.value = getErrorMessage(response);
};

/**
 * Распарсить строку и извлечь данные задачи из Weeek
 *
 * Поддерживаются форматы:
 * - Ссылка: https://app.weeek.net/ws/{workspaceId}/task/{taskId}
 * - Короткий формат: W-{taskId}
 * - Просто ID: {taskId}
 *
 * @param str Строка, содержащая задачу
 * @returns Объект с WeeekTaskId и, при наличии, WeeekTaskUrl
 */
const parseWeeekTaskString = (
  str: string,
): Pick<GetWeeekTaskModel, 'WeeekTaskId' | 'WeeekTaskUrl'> | undefined => {
  const patterns = [
    {
      // Полный URL задачи в Weeek, с workspace ID и task ID
      regex: /^https:\/\/(?:www\.)?app\.weeek\.net\/ws\/\d+\/task\/(\d+)$/,
      includeUrl: true,
    },
    {
      // Формат вида W-123456 (регистр не важен)
      regex: /^W-(\d+)$/i,
      includeUrl: false,
    },
    {
      // Просто числовой ID задачи
      regex: /^(\d+)$/,
      includeUrl: false,
    },
  ];

  for (const { regex, includeUrl } of patterns) {
    const match = str.match(regex);
    if (match) {
      const id = Number(match[1]);
      if (!isNaN(id)) {
        const result: Pick<GetWeeekTaskModel, 'WeeekTaskId' | 'WeeekTaskUrl'> = {
          WeeekTaskId: id,
        };

        // Добавляем исходную ссылку, если это был полный URL
        if (includeUrl) {
          result.WeeekTaskUrl = str;
        }

        return result;
      }
    }
  }

  // Не удалось распознать строку
  return;
};

const removeFileLinks = (description: string): string => {
  if (!description) return description;

  // Регулярное выражение для поиска всех ссылок на файлы Weeek
  const fileLinkRegex =
    /<a href="https:\/\/api\.weeek\.net\/ws\/\d+\/files\/[a-f0-9\-]+">.*?<\/a>/g;

  // Удаляем все найденные ссылки
  return description.replace(fileLinkRegex, '');
};

/**Импорт задачи */
const importTask = async () => {
  if (!inputValue.value?.trim()) {
    isError.value = true;
    return;
  }
  isLoading.value = true;

  const parsedData = parseWeeekTaskString(inputValue.value);
  if (!parsedData) {
    isError.value = true;
    isLoading.value = false;
    return;
  }

  const request = new GetWeeekTaskModel({
    ProjectId: props.projectId,
    QueueTaskId: props.queueId,
    ...parsedData,
  });
  const response = await adapter.getWeeekTask(request);
  if (!response.IsSuccess) {
    onErrorImport(response);
  } else {
    const checkExistResponse = await adapter.checkTaskExist(
      new WeeekTaskCheckExistsRequestModel({
        WeeekTaskId: response.Value.WeeekTaskId,
        ProjectId: props.projectId,
      }),
    );
    isLoading.value = false;
    if (!checkExistResponse.IsSuccess) {
      errorMessage.value = checkExistResponse.ErrorMessage;
      isError.value = true;
      return;
    }

    if (checkExistResponse.Value?.IsExists) {
      taskExist.value = checkExistResponse.Value;
      isError.value = true;
      return;
    }

    response.Value.Description = removeFileLinks(response.Value.Description);
    emit('import', response.Value);
  }
  isLoading.value = false;
};
</script>

<template>
  <div class="task-import-form">
    <TirPmInput
      class="task-import-form__input"
      v-model="inputValue"
      label="URL или ID тикета для импорта"
      :is-with-error="isError"
      :is-with-hint="!!taskExist || !!errorMessage"
      :is-show-hint="!!taskExist || !!errorMessage"
      auto-focus
      :is-disabled="isLoading"
      @update:modelValue="removeError"
      @enter="importTask"
    >
      <template #hintMessage>
        <span v-if="errorMessage">{{ errorMessage }}</span>
        <span v-else
          >Импорт по указанной ссылке уже производился. Был создан тикет:
          <RouterLink :to="taskExistRoute" target="_blank">{{ taskExist.TaskId }}</RouterLink></span
        >
      </template>
    </TirPmInput>
    <TirPmButton
      :variant="TirPmButtonVariantEnum.SecondaryState"
      :size="TirPmButtonSizeEnum.Big"
      :is-loading="isLoading"
      @click="importTask"
    >
      Импортировать
      <template #leftSlot>
        <ArrowPathIcon v-if="isReimport" />
        <ArrowDownTrayIcon v-else />
      </template>
    </TirPmButton>
  </div>
</template>

<style lang="scss" scoped>
.task-import-form {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0 1.75rem 0.75rem 1.75rem;

  &__input {
    flex: 1;
  }
}
</style>
